import { UpliftNotificationItem } from "../types/notification";

import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  signal,
  inject,
  input,
  output,
  viewChild,
} from "@angular/core";
import { AsyncPipe, DatePipe } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable, map } from "rxjs";
import { format, differenceInCalendarDays, startOfDay } from "date-fns";
import { NotificationService } from "../services/notification.service";
import { injectOptionalTransloco } from "../utils/optional-transloco";

interface UpliftNotificationItemWithSummary extends UpliftNotificationItem {
  summary: string;
  expired: boolean;
}

enum VIEW_TYPE {
  LIST = "list",
  ITEM = "item",
}

enum LIST_TYPE {
  NEW = "new",
  ARCHIVED = "archived",
}

export const NOTIFICATIONS_SESSION_STORAGE_KEY = "notificationDropdown";
const MAX_CHAR_LENGTH = 180;
const DATE_FORMAT = "yyyy-MM-dd";

@Component({
  selector: "app-my-notifications",
  templateUrl: "./my-notifications.component.html",
  styleUrl: "./my-notifications.component.scss",
  imports: [AsyncPipe, DatePipe],
  host: {
    "(document:keydown.escape)": "onEscKey()",
  },
})
export class MyNotificationsComponent implements OnInit {
  private _notificationService = inject(NotificationService);
  private _transloco = injectOptionalTransloco();
  private _destroyRef = inject(DestroyRef);

  t(key: string, fallback: string): string {
    const value = this._transloco?.translate(key);
    return value && value !== key ? value : fallback;
  }

  readonly dispatchEvent = output<void>();
  readonly panelOpenChange = output<boolean>();
  readonly notifications =
    input.required<Observable<UpliftNotificationItem[]>>();
  readonly darkThemeSupport = input(false);
  readonly scrollContainerRef =
    viewChild<ElementRef<HTMLDivElement>>("scrollContainerRef");
  notificationsList$: Observable<UpliftNotificationItemWithSummary[]>;
  readonly currentView = signal<VIEW_TYPE>(VIEW_TYPE.LIST);
  readonly listView = signal<LIST_TYPE>(LIST_TYPE.NEW);
  readonly selectedNotification = signal<UpliftNotificationItemWithSummary | null>(null);
  pingCounter = signal(false);
  isLoading = signal(false);
  isOpen = signal(false);
  isClosing = signal(false);
  counter = signal(0);
  archivedCount = signal(0);
  archivedNotificationsList$: Observable<UpliftNotificationItemWithSummary[]>;
  newNotificationsList$: Observable<UpliftNotificationItemWithSummary[]>;
  readonly currentViewNotificationsList$ = computed(() =>
    this.listView() === LIST_TYPE.NEW
      ? this.newNotificationsList$
      : this.archivedNotificationsList$,
  );
  readonly isCypressTest = !!(window as any).Cypress;
  readonly isDarkTheme = signal(false);

  ngOnInit(): void {
    if (this.darkThemeSupport()) {
      const dataTheme = document.documentElement.getAttribute("data-theme");
      const localStorageDark = localStorage.getItem("sn-dark-mode") === "true";
      this.isDarkTheme.set(dataTheme === "dark" || localStorageDark);
    }

    const sessionDate = sessionStorage.getItem(
      NOTIFICATIONS_SESSION_STORAGE_KEY,
    );

    this.notificationsList$ = this.notifications().pipe(
      map((notifications) => {
        const now = format(new Date(), DATE_FORMAT);
        return notifications.map((notification) => {
          let summary = notification.body.replace(/<[^>]*>/g, " ");
          if (summary.length > MAX_CHAR_LENGTH) {
            summary = `${summary.substring(0, MAX_CHAR_LENGTH)}…`;
          }
          return {
            ...notification,
            summary,
            expired:
              notification.expiry_date !== "" &&
              new Date(notification.expiry_date) <= new Date(now),
          };
        });
      }),
    );

    this.archivedNotificationsList$ = this.notificationsList$.pipe(
      map((notifications) =>
        notifications
          .filter(({ viewed_date }) => !!viewed_date)
          .sort(
            (a, b) =>
              new Date(b.viewed_date!).getTime() -
              new Date(a.viewed_date!).getTime(),
          ),
      ),
    );

    this.archivedNotificationsList$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((archived) => {
      this.archivedCount.set(archived.length);
    });

    this.newNotificationsList$ = this.notificationsList$.pipe(
      map((notifications) => {
        return notifications.filter(
          ({ viewed_date, expired }) => !viewed_date && !expired,
        );
      }),
    );

    this.newNotificationsList$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((notifications) => {
      this.counter.set(notifications.length);
      if (notifications.length > 0) this._setPingCounter();
      if (
        (notifications.length > 0 && !sessionDate) ||
        (sessionDate &&
          differenceInCalendarDays(
            new Date(sessionDate),
            startOfDay(new Date()),
          ) < 0)
      ) {
        sessionStorage.setItem(
          NOTIFICATIONS_SESSION_STORAGE_KEY,
          startOfDay(new Date()).toString(),
        );
      }
    });

  }

  onEscKey(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isCypressTest) return;

    if (this.darkThemeSupport()) {
      const dataTheme = document.documentElement.getAttribute("data-theme");
      const localStorageDark = localStorage.getItem("sn-dark-mode") === "true";
      this.isDarkTheme.set(dataTheme === "dark" || localStorageDark);
    }

    this.isOpen.set(true);
    this.isClosing.set(false);
    this.panelOpenChange.emit(true);
    document.documentElement.style.overflow = "hidden";
    this.setCurrentList(LIST_TYPE.NEW);
  }

  close(): void {
    this.isClosing.set(true);
    setTimeout(() => {
      this.isOpen.set(false);
      this.isClosing.set(false);
      this.panelOpenChange.emit(false);
      document.documentElement.style.overflow = "auto";
    }, 250);
  }

  backToList(): void {
    this._scrollContainerTop();
    this.currentView.set(VIEW_TYPE.LIST);
  }

  viewNotification(notification: UpliftNotificationItemWithSummary): void {
    this._scrollContainerTop();
    this.selectedNotification.set(notification);
    this.currentView.set(VIEW_TYPE.ITEM);
  }

  markNotificationAsViewed(): void {
    const selected = this.selectedNotification();
    if (!selected) return;

    this.isLoading.set(true);

    this._notificationService
      .markAsViewed([selected._id])
      .subscribe({
        next: () => {
          this.dispatchEvent.emit();
          this.backToList();
        },
        error: (error) => {
          console.error("ERROR ON MARK NOTIFICATION AS VIEWED", error);
        },
        complete: () => this.isLoading.set(false),
      });
  }

  setCurrentList(listType: string): void {
    this.listView.set(listType as LIST_TYPE);
    if (this.currentView() === VIEW_TYPE.ITEM) {
      this.backToList();
    } else {
      this._scrollContainerTop();
    }
  }

  private _setPingCounter(): void {
    this.pingCounter.set(true);
    setTimeout(() => this.pingCounter.set(false), 7000);
  }

  private _scrollContainerTop() {
    this.scrollContainerRef()?.nativeElement.scrollTo({ top: 0 });
  }
}
